import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ContentChildren,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  inject,
} from '@angular/core';
import {
  DocDrawerTemplateDirective,
  DocDrawerTemplateType,
} from './doc-drawer-template.directive';

export type DocDrawerPosition = 'left' | 'right' | 'top' | 'bottom';

let openDrawerCount = 0;

@Component({
  selector: 'doc-drawer',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (visible) {
      @if (showMask) {
        <div
          class="doc-drawer-mask"
          [style.z-index]="zIndex"
          (click)="onMaskClick($event)"></div>
      }

      <aside
        class="doc-drawer-panel doc-drawer-panel--{{ position }}"
        [ngClass]="styleClass"
        [ngStyle]="panelStyle"
        [style.z-index]="zIndex + 1"
        role="dialog"
        aria-modal="true"
        (click)="$event.stopPropagation()">
        @if (showHeaderSection) {
          <div class="doc-drawer-header">
            @if (getTemplate('header'); as headerTpl) {
              <div class="doc-drawer-header-content">
                <ng-container *ngTemplateOutlet="headerTpl"></ng-container>
              </div>
            } @else if (header) {
              <div class="doc-drawer-header-content">
                <span class="doc-drawer-title">{{ header }}</span>
              </div>
            }

            @if (closable !== false) {
              <button
                type="button"
                class="doc-drawer-close"
                aria-label="Close"
                (click)="close($event)">
                <i class="pi pi-times"></i>
              </button>
            }
          </div>
        }

        <div class="doc-drawer-content" [ngStyle]="contentStyle">
          @if (getTemplate('content'); as contentTpl) {
            <ng-container *ngTemplateOutlet="contentTpl"></ng-container>
          } @else {
            <ng-content></ng-content>
          }
        </div>

        @if (getTemplate('footer'); as footerTpl) {
          <div class="doc-drawer-footer">
            <ng-container *ngTemplateOutlet="footerTpl"></ng-container>
          </div>
        }
      </aside>
    }
  `,
  styleUrl: './doc-drawer.component.scss',
})
export class DocDrawerComponent implements OnChanges, OnDestroy {
  private readonly document = inject(DOCUMENT);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onHide = new EventEmitter<Event | undefined>();

  @Input() position: DocDrawerPosition = 'right';
  @Input() modal: boolean | string = true;
  @Input() dismissible = true;
  @Input() closeOnEscape = true;
  @Input() closable = true;
  @Input() showHeader = true;
  @Input() header?: string;
  @Input() style?: Record<string, string>;
  @Input() contentStyle?: Record<string, string>;
  @Input() styleClass = '';
  @Input() baseZIndex = 1100;
  @Input() blockScroll = true;

  @ContentChildren(DocDrawerTemplateDirective)
  private templates!: QueryList<DocDrawerTemplateDirective>;

  @HostBinding('class.doc-drawer-open')
  get isOpen(): boolean {
    return this.visible;
  }

  @HostBinding('class')
  get hostClass(): string {
    return 'doc-drawer-host';
  }

  get zIndex(): number {
    return this.baseZIndex ?? 1100;
  }

  get showMask(): boolean {
    return this.modal !== false && this.modal !== 'false';
  }

  get panelStyle(): Record<string, string> | null {
    return this.style ?? null;
  }

  get showHeaderSection(): boolean {
    if (this.showHeader === false) {
      return false;
    }

    return !!(this.header || this.getTemplate('header'));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['visible']) {
      return;
    }

    if (this.visible) {
      this.lockScroll();
      return;
    }

    this.unlockScroll();
  }

  ngOnDestroy(): void {
    if (this.visible) {
      this.unlockScroll();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (!this.visible || this.closeOnEscape === false) {
      return;
    }

    this.close(event);
  }

  onMaskClick(event: MouseEvent): void {
    if (this.dismissible === false) {
      return;
    }

    this.close(event);
  }

  close(event?: Event): void {
    if (!this.visible) {
      return;
    }

    this.visible = false;
    this.visibleChange.emit(false);
    this.onHide.emit(event);
    this.unlockScroll();
  }

  getTemplate(type: DocDrawerTemplateType): TemplateRef<unknown> | null {
    return this.templates?.find((template) => template.type === type)?.template ?? null;
  }

  private lockScroll(): void {
    if (!this.blockScroll) {
      return;
    }

    openDrawerCount += 1;
    this.document.body.classList.add('doc-drawer-scroll-lock');
  }

  private unlockScroll(): void {
    if (!this.blockScroll) {
      return;
    }

    openDrawerCount = Math.max(0, openDrawerCount - 1);
    if (openDrawerCount === 0) {
      this.document.body.classList.remove('doc-drawer-scroll-lock');
    }
  }
}
